import { BusinessUnitsTableResult } from 'src/apps/dashboard/services/queries/business-units-table/helpers/transformConsolidatedResults';
import { IBusinessUnitRow, IScoreData } from 'src/apps/dashboard/types/BusinessUnitsTableTypes';
import { BusinessUnit } from 'src/apps/dashboard/types/DashboardTypes';
import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import {
	calculateRecentPeriodDates,
	formatDateForGraphQL,
	getDateTimestamp
} from 'src/common/helpers/DateFormatHelper';

import {
	MobileBusinessUnitsTableDataQuery,
	MobileBusinessUnitsTableDataQueryVariables
} from '../MobileBusinessUnitsTable.generated';

import { getMobileAppIdsFromFilters, getPlatformFromFilters } from './mobileFilterVariableBuilder';

// score_mobile_products exposes "" rows as all-platforms / all-apps rollups. When no
// platform/app filter is active we read those rollup rows; otherwise we read the slice
// matching the selection.
const ROLLUP_VALUE = '';

type ScoreMobileProduct = NonNullable<
	NonNullable<MobileBusinessUnitsTableDataQuery['score_mobile_products']>[number]
>;

const DEFAULT_SCORE_DATA: IScoreData = { value: 0, percentage: 0 };

const roundToOneDecimal = (value: number): number => Math.round(value * 10) / 10;

// score is a 0..1 accessibility score (same scale as score_web_products); surface it as 0..100.
// percentage stays 0: mobile has no prior-period delta yet, and the table reads `value`.
const formatScore = (score?: number): IScoreData => {
	if (score == null) {
		return DEFAULT_SCORE_DATA;
	}

	const clampedScore = Math.min(Math.max(score * 100, 0), 100);
	return { value: roundToOneDecimal(clampedScore), percentage: 0 };
};

// Each unit may have several daily snapshots in the requested range; keep the latest.
const pickLatestProductPerUnit = (
	products: ScoreMobileProduct[]
): Map<string, ScoreMobileProduct> => {
	const latestByUnit = new Map<string, ScoreMobileProduct>();

	products.forEach((product) => {
		const unitId = product.unit_id?.trim();
		if (!unitId) {
			return;
		}

		const existing = latestByUnit.get(unitId);
		if (
			!existing ||
			getDateTimestamp(product.calculation_date) > getDateTimestamp(existing.calculation_date)
		) {
			latestByUnit.set(unitId, product);
		}
	});

	return latestByUnit;
};

export const buildMobileBusinessUnitsTableVariables = (
	days: number,
	filters?: IFilterOptions
): MobileBusinessUnitsTableDataQueryVariables => {
	const { today: todayDate, recentPeriodStart } = calculateRecentPeriodDates(days);
	const platform = getPlatformFromFilters(filters);
	const appIds = getMobileAppIdsFromFilters(filters);

	return {
		days,
		calculationDateFrom: { value: [formatDateForGraphQL(recentPeriodStart)] },
		calculationDateTo: { value: [formatDateForGraphQL(todayDate)] },
		platformFilter:
			platform === 'all' && appIds.length
				? undefined
				: { value: [platform === 'all' ? ROLLUP_VALUE : platform] },
		appIdFilter: { value: appIds.length ? appIds : [ROLLUP_VALUE] }
	};
};

export const transformMobileBusinessUnitsTableResults = (
	rawResults: MobileBusinessUnitsTableDataQuery,
	businessUnits: BusinessUnit[]
): BusinessUnitsTableResult => {
	const products = (rawResults.score_mobile_products ?? []).filter(
		(product): product is ScoreMobileProduct => product !== null
	);
	const latestByUnit = pickLatestProductPerUnit(products);

	const rows: IBusinessUnitRow[] = businessUnits.map((businessUnit) => {
		const product = latestByUnit.get(businessUnit.id);
		const score = formatScore(product?.score);

		return {
			id: businessUnit.id,
			name: businessUnit.name,
			criticalIssues: product?.unique_issues_critical ?? 0,
			seriousIssues: product?.unique_issues_serious ?? 0,
			uniqueIssues: product?.unique_issues ?? 0,
			score,
			scoreValue: score.value
		};
	});

	return {
		rows,
		total: rows.length,
		unfilteredTotal: rows.length,
		scoreWebProducts: [],
		aggregatedScoreWebProducts: [],
		aggregatedScoreDeltas: []
	};
};
